import React, { useEffect, useMemo, useState } from 'react';

import { combineReducers } from '@reduxjs/toolkit';
import { apiMetaResetAction } from 'actions/apiPaginationMeta';
import { API_STATUSES } from 'actions/apiStatus';
import { onPrimaryAction } from 'actions/continuousFeedback';
import { fetchFeedbacksAction } from 'actions/feedbacks';
import { resetAllAction } from 'actions/globalFilters';
import { fetchReviewsAction } from 'actions/meshlistReviews';
import { tabUpdateSelectionAction } from 'actions/tabs';
import { addNewTaskAction, fetchGoalsAction, fetchTasksAction } from 'actions/task';
import { ModularCardWithHeader } from 'components/CardWithHeader';
import useForceUpdate from 'components/hooks/useForceUpdate';
import useReducerWithThunk from 'components/hooks/useReducerWithThunk';
import Task1 from 'models/task1';
import { batch, useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import apiPaginationMetaReducer, { getApiPaginationMetaInitialState } from 'reducers/apiPaginationMeta';
import apiStatusReducer, { apiStatusInitialState } from 'reducers/apiStatus';
import feedbacksReducer from 'reducers/feedbacks';
import reviewsReducer from 'reducers/meshlistReviews';
import tasksReducer from 'reducers/task';
import {
  completedOnFilterReducer,
  buildCompletedOnFilterInitialState,
} from 'screens/Team/TeamPageFilters/CompletedOnFilter';
import { employeeFilterReducer, EMPLOYEE_FILTER_UPDATE } from 'screens/Team/TeamPageFilters/EmployeeFilter';
import CreateCheckInSidepanel from 'screens/admin/CheckIn/CreateCheckInSidepanel';
import { selectMeshListGlobalFilters } from 'selectors/globalFilters';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { checkLevelUpFeatureFlagEnabled } from 'utils/helpers/featureFlags';
import {
  checkAnyTeamPageModuleEnabled,
  checkFeedbackModuleEnabled,
  checkGoalModuleEnabled,
  checkOneOnOneModuleEnabled,
  checkReviewModuleEnabled,
  checkTaskModuleEnabled,
} from 'utils/helpers/moduleAccess';

import { FEEDBACK_FORM_TYPE, MESH_LIST_TABS, PAGE_TYPES } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';
import { localStorageKeys } from 'constants/localStorageKeys';
import { sendClickedOnLevelUpTabEvent, sendViewTabsOfHomepageEvent } from 'constants/mixpanelHelper';

import { getStartEndTimeFromDurationKey } from 'services/helpers/task';

import MeshListContext from './MeshListContext';
import { MeshListHeader } from './MeshListHeader';
import SectionLoadingView from './SectionLoadingView';
import TabHeader from './TabHeader';
import CheckIns from './checkIns/CheckIns';
import Competencies from './competencies';
import PastFeedbacks from './feedbacks';
import CreateGoalPanel from './goals/CreateGoalPanel';
import Goals from './goals/Goals';
import { getEnabledMeshListTabItems, isEnabledTabItemId } from './helpers';
import LevelUp from './levelUp';
import PastReviews from './reviews';
import tabReducer, { tabReducerInitialState } from './tabReducer';
import TaskFiltersPanel from './tasks/TaskFiltersPanel';
import Tasks from './tasks/Tasks';
import tasksFiltersReducer, { tasksFiltersInitialState } from './tasks/tasksFiltersReducer';

const { startTS, endTS } = getStartEndTimeFromDurationKey('LASTYEAR');

const filterInitialState = {
  completedOnFilter: buildCompletedOnFilterInitialState({
    from: new Date(startTS),
    to: new Date(endTS),
  }),
  employeeFilter: {},
};

// make default selected tab as goals
const tabStateWithGoalSelected = { ...tabReducerInitialState, selected: MESH_LIST_TABS.goal.id };

const meshListReducerInitialState = {
  tabs: tabStateWithGoalSelected,
  filters: filterInitialState,
  tasks: [],
  apiStatus: apiStatusInitialState,
  apiPaginationMeta: getApiPaginationMetaInitialState(),
  tasksFilters: tasksFiltersInitialState,
  feedbacks: [],
  reviews: [],
};

function buildMeshInitialState(
  employeeId,
  isMsteams = false,
  msTeamsSelectedTab = 'TASK',
  pageType,
  showLevelUpTab = false
) {
  // read selected tab from local storage if present, otherwise set it to goals

  let selectedMeshListTabFromLS = Number(localStorage.getItem(localStorageKeys.selectedMeshListTab));

  if (!isEnabledTabItemId(selectedMeshListTabFromLS)) {
    // module not enabled, so not valid anymore
    selectedMeshListTabFromLS = null;
    localStorage.removeItem(localStorageKeys.selectedMeshListTab);
  }

  const isLevelUpSelected = selectedMeshListTabFromLS === MESH_LIST_TABS.levelUp.id;
  const isCompetenciesSelected = selectedMeshListTabFromLS === MESH_LIST_TABS.competencies.id;

  if (isLevelUpSelected || isCompetenciesSelected) {
    if (pageType !== PAGE_TYPES.oneOnOne) {
      selectedMeshListTabFromLS = null;
      localStorage.removeItem(localStorageKeys.selectedMeshListTab);
    } else if (isLevelUpSelected) {
      if (!checkLevelUpFeatureFlagEnabled() || !showLevelUpTab) {
        selectedMeshListTabFromLS = null;
        localStorage.removeItem(localStorageKeys.selectedMeshListTab);
      }
    } else if (isCompetenciesSelected) {
      if (showLevelUpTab && checkLevelUpFeatureFlagEnabled()) {
        selectedMeshListTabFromLS = null;
        localStorage.removeItem(localStorageKeys.selectedMeshListTab);
      }
    }
  }

  if (
    checkAnyTeamPageModuleEnabled() ||
    (pageType === PAGE_TYPES.profile && checkReviewModuleEnabled()) ||
    (pageType === PAGE_TYPES.oneOnOne && checkFeedbackModuleEnabled())
  ) {
    const selectedTab = isMsteams
      ? msTeamsSelectedTab === 'TASK'
        ? MESH_LIST_TABS.task.id
        : MESH_LIST_TABS.goal.id
      : selectedMeshListTabFromLS || getEnabledMeshListTabItems()[0].id;
    if (selectedTab) {
      return addCurrentEmployeeFilter(
        {
          ...meshListReducerInitialState,
          tabs: {
            ...meshListReducerInitialState.tabs,
            selected: selectedTab,
          },
        },
        employeeId
      );
    }
  }
  return addCurrentEmployeeFilter(meshListReducerInitialState, employeeId);
}

function addCurrentEmployeeFilter(state, employeeId) {
  const newState = { ...state };
  const employeeFilter = { [employeeId]: true };
  newState.filters = { ...state.filters, employeeFilter };
  return newState;
}

const filterReducer = combineReducers({
  completedOnFilter: completedOnFilterReducer,
  employeeFilter: employeeFilterReducer,
});

const meshListReducer = combineReducers({
  tabs: tabReducer,
  filters: filterReducer,
  tasks: tasksReducer,
  apiPaginationMeta: apiPaginationMetaReducer,
  apiStatus: apiStatusReducer,
  tasksFilters: tasksFiltersReducer,
  feedbacks: feedbacksReducer,
  reviews: reviewsReducer,
});

// TODO: There will be meshlist home page and meshlist team view, two separate views
// and this one will be modular
export default function MeshList({
  employee,
  pageType = PAGE_TYPES.home,
  headerText = 'Mesh List',
  isMsteams = false,
  msTeamsSelectedTab = 'TASK',
  forceLoggedInEmployeeAsFalse = false,
  showLevelUpTab = false,
  levelUpProps = {},
  headerComponent = null,
}) {
  /*
    Use task reducer
    Things like filter, stats, that will be separate reducers
    I'll call apis inside action and update reducers
    I want to use normalization
    I will use context api to pass data to children
  */
  const [state, dispatch] = useReducerWithThunk(
    meshListReducer,
    buildMeshInitialState(employee.id, isMsteams, msTeamsSelectedTab, pageType, showLevelUpTab)
  );

  const reduxDispatch = useDispatch();
  const history = useHistory();

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const globalFilters = useSelector(selectMeshListGlobalFilters);

  const [forceUpdateValue, forceUpdate] = useForceUpdate();
  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);
  const [createNewInProgress, setCreateNewInProgress] = useState(false);

  const isLoggedInEmployee = !forceLoggedInEmployeeAsFalse ? loggedInEmployee?.id === employee?.id : false;

  const {
    tasks = [],
    tabs,
    apiStatus,
    apiPaginationMeta,
    tasksFilters = {},
    filters,
    feedbacks = [],
    reviews = [],
  } = state;

  const selectedTab = isMsteams
    ? msTeamsSelectedTab === 'TASK'
      ? MESH_LIST_TABS.task.id
      : MESH_LIST_TABS.goal.id
    : tabs?.selected;

  if (!isLoggedInEmployee && selectedTab === MESH_LIST_TABS.checkIn.id) {
    // this check forces tasks tab by default for other employees profile page.
    // keeping it default to goal.
    dispatch(tabUpdateSelectionAction(MESH_LIST_TABS.goal.id));
  }

  if (isLoggedInEmployee && (selectedTab === MESH_LIST_TABS.feedback.id || selectedTab === MESH_LIST_TABS.review.id)) {
    // this check forces tasks tab by default for other employees profile page.
    // keeping it default to goal.
    dispatch(tabUpdateSelectionAction(MESH_LIST_TABS.goal.id));
  }

  // disabling feedback tab in 1:1s
  // if (pageType === PAGE_TYPES.profile && selectedTab === MESH_LIST_TABS.feedback.id) {
  if (selectedTab === MESH_LIST_TABS.feedback.id) {
    dispatch(tabUpdateSelectionAction(MESH_LIST_TABS.goal.id));
  }

  if (pageType === PAGE_TYPES.oneOnOne && selectedTab === MESH_LIST_TABS.review.id) {
    dispatch(tabUpdateSelectionAction(MESH_LIST_TABS.goal.id));
  }

  const apiState = apiStatus.state;

  const contextValue = useMemo(
    () => ({
      selectedTab,
      dispatch,
      employee,
      apiPaginationMeta,
      tasksFilters,
      setCreateNewInProgress,
    }),
    [selectedTab, dispatch, JSON.stringify(employee)]
  );

  useEffect(() => () => reduxDispatch(resetAllAction()), []);

  useEffect(() => {
    batch(() => {
      reduxDispatch(resetAllAction()).then(() => {
        if (selectedTab === MESH_LIST_TABS.feedback.id && pageType === PAGE_TYPES.oneOnOne) {
          dispatch(fetchFeedbacksAction({ receiverEmployeeId: employee?.id }));
        } else if (selectedTab === MESH_LIST_TABS.review.id && pageType === PAGE_TYPES.profile) {
          dispatch(fetchReviewsAction({ receiverEmployeeId: employee?.id }));
        } else if (selectedTab === MESH_LIST_TABS.levelUp.id || selectedTab === MESH_LIST_TABS.competencies.id) {
        } else if (selectedTab === MESH_LIST_TABS.goal.id) {
          dispatch(fetchGoalsAction({ globalFilters, pageType, employeeId: employee.id }));
        } else {
          dispatch(fetchTasksAction({ globalFilters, pageType, employeeId: employee.id }));
        }
      });
    });
  }, [selectedTab]);

  // `filters` is a dependency for profile page, where employee gets changed.
  // This is required as `fetchTasksAction` get's employee id to request data from reducer store
  // `employeefilters` within `filters`
  useEffect(() => {
    batch(() => {
      if (selectedTab === MESH_LIST_TABS.feedback.id && pageType === PAGE_TYPES.oneOnOne) {
        dispatch(fetchFeedbacksAction({ receiverEmployeeId: employee?.id }));
      } else if (selectedTab === MESH_LIST_TABS.review.id && pageType === PAGE_TYPES.profile) {
        dispatch(fetchReviewsAction({ receiverEmployeeId: employee?.id }));
      } else if (selectedTab === MESH_LIST_TABS.levelUp.id || selectedTab === MESH_LIST_TABS.competencies.id) {
      } else if (selectedTab === MESH_LIST_TABS.goal.id) {
        dispatch(fetchGoalsAction({ globalFilters, pageType, employeeId: employee.id }));
      } else {
        dispatch(fetchTasksAction({ globalFilters, employeeId: employee.id }));
      }
    });
  }, [forceUpdateValue, JSON.stringify(filters)]);

  // Whenever employee prop gets changed we need to update that in reducer store too,
  // as API request gets employee id from reducer store
  useEffect(() => {
    dispatch({ type: EMPLOYEE_FILTER_UPDATE, values: { [employee.id]: true } });
  }, [JSON.stringify(employee)]);

  const onTabChange = (newViewTypeId) => {
    const tabType = Object.values(MESH_LIST_TABS).filter(({ id }) => id === newViewTypeId)?.[0]?.displayName;
    sendViewTabsOfHomepageEvent({
      eventProperties: {
        type: tabType,
      },
    });
    if (newViewTypeId === MESH_LIST_TABS.levelUp.id) {
      sendClickedOnLevelUpTabEvent({
        eventProperties: {
          source: '1:1',
        },
      });
    }
    setCreateNewInProgress(false);
    reduxDispatch(resetAllAction()).then(() => {
      if (selectedTab !== newViewTypeId) {
        localStorage.setItem(localStorageKeys.selectedMeshListTab, newViewTypeId);
        dispatch(apiMetaResetAction(getApiPaginationMetaInitialState()));
        dispatch(tabUpdateSelectionAction(newViewTypeId));
      }
    });
  };

  const createNewHandler = ({ feedbackType = null }) => {
    setCreateNewInProgress(true);
    if (selectedTab === MESH_LIST_TABS.goal.id) {
      // goals selected
      setIsOpenCreateGoalPanel(true);
    }
    if (selectedTab === MESH_LIST_TABS.task.id) {
      dispatch(
        addNewTaskAction({
          employee: { ...employee, ownerType: Task1.EMPLOYEE_TYPES.owner },
          selectedTeamId: tasksFilters.team,
          dueDate: new Date(),
        })
      );
    }

    if (selectedTab === MESH_LIST_TABS.feedback.id) {
      if (feedbackType === FEEDBACK_FORM_TYPE.PROVIDED) {
        reduxDispatch(onPrimaryAction(forceUpdate));
      }

      const feedbackParamsObj = {
        history,
        formType: feedbackType,
        newForm: true,
      };

      if (feedbackType === FEEDBACK_FORM_TYPE.PROVIDED) {
        feedbackParamsObj.empId = employee.id;
      }

      pushFeedbackParams(feedbackParamsObj);
      setCreateNewInProgress(false);
    }
  };

  const isLoading = apiState === API_STATUSES.loading;
  const isLoadingMore = apiState === API_STATUSES.loadingMore;

  const isLevelUpSelected = selectedTab === MESH_LIST_TABS.levelUp.id;
  const isCompetenciesSelected = selectedTab === MESH_LIST_TABS.competencies.id;

  const isLevelUpTabApplicable = showLevelUpTab && isLevelUpSelected && checkLevelUpFeatureFlagEnabled();

  const isCompetenciesTabApplicable = isCompetenciesSelected;

  // oneOnOne sidepanel
  const [isOneOnOnePanelOpen, setIsOneOnOnePanelOpen] = useState(false);

  if (isMsteams) {
    return (
      <MeshListContext.Provider value={contextValue}>
        <StyledCardView disabled={isLoading || isLoadingMore}>
          <MeshListHeader
            createNewInProgress={createNewInProgress}
            createNewHandler={createNewHandler}
            employee={employee}
            refreshHandler={forceUpdate}
            headerText={headerText}
            isMsteams
            headerComponent={headerComponent}
          />
          <ContentContainer>
            {isLoading ? (
              <SectionLoadingView />
            ) : (
              <TabViewContainer>
                {msTeamsSelectedTab === 'TASK' ? (
                  <Tasks tasks={tasks} pageType={pageType} employee={employee} />
                ) : (
                  <Goals
                    goals={tasks}
                    pageType={pageType}
                    employee={employee}
                    setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
                    source="Mesh List"
                  />
                )}

                {isLoadingMore && <SectionLoadingView />}
              </TabViewContainer>
            )}
          </ContentContainer>
        </StyledCardView>
        {isOpenCreateGoalPanel && (
          <CreateGoalPanel
            open={isOpenCreateGoalPanel}
            onCancel={() => {
              setIsOpenCreateGoalPanel(false);
              setCreateNewInProgress(false);
            }}
            goalListDispatch={dispatch}
            employee={employee}
            eventSource="Mesh List"
          />
        )}
        <CreateCheckInSidepanel
          isOpen={isOneOnOnePanelOpen}
          setIsOpen={setIsOneOnOnePanelOpen}
          currentOneOnOneEmployees={[]}
          loggedInEmployeeId={loggedInEmployee.id}
          forceUpdateParent={forceUpdate}
        />
      </MeshListContext.Provider>
    );
  }

  return (
    <MeshListContext.Provider value={contextValue}>
      {/* 
          data related to level up is not handled by meshlist reducer.
          so skipping loading if levelUp tab is selected.
        */}
      <StyledCardView disabled={isLevelUpSelected || isCompetenciesSelected ? false : isLoading || isLoadingMore}>
        <MeshListHeader
          createNewInProgress={createNewInProgress}
          createNewHandler={createNewHandler}
          employee={employee}
          refreshHandler={forceUpdate}
          headerText={headerText}
          headerComponent={headerComponent}
        />
        <ContentContainer>
          <StyledTabHeader
            currentTab={selectedTab}
            onTabChange={onTabChange}
            isLoggedInEmployee={isLoggedInEmployee}
            isOneOnOne={PAGE_TYPES.oneOnOne === pageType}
            showLevelUpTab={showLevelUpTab}
          />
          {/* is levelUp is selected render only that */}

          {pageType === PAGE_TYPES.oneOnOne && (isLevelUpTabApplicable || isCompetenciesTabApplicable) ? (
            <TabViewContainer onScroll={(e) => e.stopPropagation()}>
              {isLevelUpTabApplicable ? (
                <LevelUp
                  employee={employee}
                  refreshHandler={forceUpdate}
                  {...levelUpProps}
                  isOneOnOne={PAGE_TYPES.oneOnOne === pageType}
                />
              ) : (
                <Competencies employee={employee} />
              )}
            </TabViewContainer>
          ) : isLoading ? (
            <SectionLoadingView />
          ) : tasksFilters.isFilterViewOpen ? (
            <TasksFiltersWrapper>
              <TaskFiltersPanel employee={employee} forceUpdateParent={forceUpdate} />
            </TasksFiltersWrapper>
          ) : (
            <TabViewContainer onScroll={(e) => e.stopPropagation()}>
              {checkTaskModuleEnabled() && <Tasks tasks={tasks} pageType={pageType} employee={employee} />}
              {checkGoalModuleEnabled() && (
                <Goals
                  goals={tasks}
                  pageType={pageType}
                  employee={employee}
                  setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
                  source="Mesh List"
                />
              )}
              {checkOneOnOneModuleEnabled() && (
                <CheckIns
                  tasks={tasks}
                  pageType={pageType}
                  employee={employee}
                  setIsOneOnOnePanelOpen={setIsOneOnOnePanelOpen}
                />
              )}
              {checkFeedbackModuleEnabled() && (
                <PastFeedbacks
                  feedbacks={feedbacks}
                  pageType={pageType}
                  employee={employee}
                  loggedInEmployee={loggedInEmployee}
                  refreshHandler={forceUpdate}
                />
              )}
              {checkReviewModuleEnabled() && (
                <PastReviews
                  reviews={reviews}
                  pageType={pageType}
                  employee={employee}
                  loggedInEmployee={loggedInEmployee}
                  refreshHandler={forceUpdate}
                />
              )}
              {isLoadingMore && <SectionLoadingView />}
            </TabViewContainer>
          )}
        </ContentContainer>
      </StyledCardView>
      {isOpenCreateGoalPanel && (
        <CreateGoalPanel
          open={isOpenCreateGoalPanel}
          onCancel={() => {
            setIsOpenCreateGoalPanel(false);
            setCreateNewInProgress(false);
          }}
          goalListDispatch={dispatch}
          employee={employee}
          eventSource="Mesh List"
        />
      )}
      <CreateCheckInSidepanel
        isOpen={isOneOnOnePanelOpen}
        setIsOpen={setIsOneOnOnePanelOpen}
        currentOneOnOneEmployees={[]}
        loggedInEmployeeId={loggedInEmployee.id}
        forceUpdateParent={forceUpdate}
      />
    </MeshListContext.Provider>
  );
}

const TasksFiltersWrapper = styled.div`
  height: calc(100% - 65px);
`;

const CardView = styled(ModularCardWithHeader)`
  height: 99.5%;
`;

const ContentContainer = styled.div`
  height: calc(100% - 65px);
`;

const StyledTabHeader = styled(TabHeader)`
  height: 50px;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const TabViewContainer = styled.div`
  height: calc(100% - 50px);
`;

const StyledCardView = styled(CardView)`
  ${(props) =>
    props.disabled &&
    css`
      cursor: initial;
      opacity: 0.65;
      pointer-events: none;
    `}
`;
